import { makeStyles } from '@material-ui/core/styles';
import { Handle, Position } from '@xyflow/react';
import classnames from 'classnames';
import hexToRgba from 'hex-rgba';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import { ModalType, NodeType, ScheduleType } from '../../app/types';
import IconClock from '../../assets/images/Icons/IconClock';
import icDelete20 from '../../assets/images/svg/ic-delete-20.svg';
import {
  checkNode,
  deleteNode,
  setDefaultTemplate,
  setIsShowSettingBar,
  setSelectedNode,
} from '../../features/mainSlice';
import { openModal } from '../../features/modalSlice';

const useStyles = makeStyles(
  {
    root: {
      width: 240,
      borderRadius: '12px',
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      border: 0,
    },
    selected: {
      border: `solid 1px ${color.$24}`,
    },
    error: {
      border: `solid 1px ${color.$17}`,
    },
    highlightPath: {
      border: `solid 1px ${color.$3}!important`,
    },
    handle: {
      border: '0 !important',
      borderRadius: '0 !important',
      background: `${color.$3} !important`,
      width: '1px !important',
      height: '1px !important',
      minWidth: '1px !important',
      minHeight: '1px !important',
    },
    title: {
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 14px 8px 16px',
      background: hexToRgba(color.$24, 6),
      '& div': {
        display: 'flex',
        alignItems: 'center',
      },
      '& > svg': {
        marginRight: 2,
      },
    },
    errorTitle: {
      color: color.$17,
      background: hexToRgba(color.$17, 6),
    },
    content: {
      padding: '12px 16px',
    },
    value: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px',
      background: color.$19,
      minHeight: 25,
      gap: 2,
    },
    iconInbox: {
      marginRight: 4,
    },
    valueText: {
      display: '-webkit-box',
      overflow: 'hidden',
      wordBreak: 'break-all',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: '1',
    },
    nowrap: {
      whiteSpace: 'nowrap',
    },
  },
  {
    name: 'Schedule',
  },
);

const ScheduleComponent = ({ id, data, isConnectable }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isErrorNodeIds = useSelector(store => store.ma.isErrorNodeIds);
  const selectedNode = useSelector(store => store.ma.selectedNode);
  const isActive = useSelector(store => store.ma.isActive);
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const allPathNodes = useSelector(store => store.ma.highlightPathNodes) || [];
  const isPreview = useSelector(store => store.ma.isPreview);
  const isHighlightPath = isPreview && !!allPathNodes.find(n => n?.id === id);
  const { type, time, week, month, hasLastDay } = data || {};
  const [isHover, setIsHover] = useState(false);

  const isSelected = selectedNode?.id === id;
  const isError =
    !!~isErrorNodeIds.indexOf(id) &&
    !checkNode({ type: NodeType.SCHEDULE, data });

  const dayText = useMemo(() => {
    switch (type) {
      case ScheduleType.WEEK:
        if (!week.length) {
          return (
            <div className={classes.valueText}>
              {t('message-node.not-wait')}
            </div>
          );
        }

        return (
          <>
            <div className={classes.nowrap}>{t(`scheduleType.${type}`)}</div>
            <div className={classes.valueText}>
              {[...week].map(w => t(`week${w}`))}
            </div>
            <div className={classes.nowrap}>{time}</div>
          </>
        );
      case ScheduleType.MONTH:
        if (!month.length) {
          if (hasLastDay) {
            return (
              <div className={classes.valueText}>{t('hasLastDayDisplay')}</div>
            );
          }

          return (
            <div className={classes.valueText}>
              {t('message-node.not-wait')}
            </div>
          );
        }

        return (
          <>
            <div className={classes.nowrap}>{t(`scheduleType.${type}`)}</div>
            <div className={classes.valueText}>{[...month].join(',')}</div>
            <div className={classes.nowrap}>{time}</div>
          </>
        );
      default:
        return (
          <>
            <div className={classes.nowrap}>{t(`scheduleType.${type}`)}</div>
            <div className={classes.valueText} />
            <div className={classes.nowrap}>{time}</div>
          </>
        );
    }
  }, [data]);

  const onSelect = () => {
    if (!isSelected) {
      dispatch(setSelectedNode(nodes.find(n => n?.id === id)));
      dispatch(
        setDefaultTemplate({
          isShow: false,
        }),
      );
    }

    dispatch(setIsShowSettingBar(true));
  };

  function onDelete() {
    dispatch(
      openModal({
        modalType: ModalType.DIALOG_TEXT,
        modalProps: {
          title: t('delete-schedule-node-modal.title'),
          content: t('delete-schedule-node-modal.content'),
          cancelText: t('delete-schedule-node-modal.cancelText'),
          submitText: t('delete-schedule-node-modal.submitText'),
        },
        onClick: () => {
          dispatch(deleteNode(id));
        },
      }),
    );
  }

  return (
    <div>
      <Handle
        className={classes.handle}
        type="target"
        position={Position.Top}
        style={{ top: 0 }}
        isConnectable={isConnectable}
      />
      <div
        className={classnames(classes.root, {
          [classes.selected]: isSelected,
          [classes.error]: isError,
          [classes.highlightPath]: isHighlightPath,
        })}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={onSelect}
      >
        <div
          className={classnames(classes.title, {
            [classes.errorTitle]: isError,
          })}
        >
          <div>
            <IconClock
              className={classes.iconInbox}
              backgroundColor={isError ? color.$17 : color.$24}
            />
            {t('schedule')}
          </div>
          {isHover && !isActive && (
            <img
              style={{ cursor: 'pointer' }}
              onClick={() => onDelete()}
              src={icDelete20}
              alt="delete"
            />
          )}
        </div>
        <div className={classes.content}>
          <div className={classes.value}>{dayText}</div>
        </div>
      </div>
      <Handle
        className={classes.handle}
        type="source"
        position={Position.Bottom}
        style={{ bottom: 0 }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default ScheduleComponent;
